import { useContext, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import CircularProgress from '@mui/material/CircularProgress'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { ToastNotificationContext } from '../../../global/context/toast-context/ToastNotificationContext'
import { uploadDocument } from '../../api'
import styles from './styles.module.css'

interface UploadModalProps {
  isOpen: boolean
  onClose: () => void
  onUploadComplete: () => void
}

function UploadModal({ isOpen, onClose, onUploadComplete }: UploadModalProps) {
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [file, setFile] = useState<File | null>(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [isUploading, setIsUploading] = useState(false)

  function resetForm() {
    setFile(null)
    setName('')
    setDescription('')
    setIsUploading(false)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  function handleClose() {
    if (isUploading) return
    resetForm()
    onClose()
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = e.target.files?.[0]
    if (selected) {
      setFile(selected)
      if (!name) {
        setName(selected.name.replace(/\.[^/.]+$/, ''))
      }
    }
  }

  async function handleUpload() {
    if (!file || !name) return

    setIsUploading(true)

    try {
      const result = await uploadDocument(file, name, description || undefined)

      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: `Document uploaded successfully (${result.totalChunks} chunks).`,
        severity: 'success',
      })
      resetForm()
      onUploadComplete()
      onClose()
    } catch (error: any) {
      setIsUploading(false)
      setToastStatus({
        ...toastStatus,
        isOpen: true,
        message: error.message || 'Upload failed.',
        severity: 'error',
      })
    }
  }

  const isFormValid = file && name

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box className={styles.uploadModalContainer}>
        <div className={styles.title}>Upload Document</div>

        <div className={styles.formContainer}>
          <input
            ref={fileInputRef}
            type='file'
            accept='.pdf,.docx,.doc,.txt'
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />

          <SecondaryButton
            buttonName='Select File'
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          />
          {file && <span className={styles.fileName}>{file.name}</span>}

          <TextField
            label='Document Name'
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size='small'
            disabled={isUploading}
          />

          <TextField
            label='Description (optional)'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            size='small'
            multiline
            rows={2}
            disabled={isUploading}
          />
        </div>

        {isUploading && (
          <div className={styles.progressContainer}>
            <CircularProgress size={20} />
            <span>Uploading and processing...</span>
          </div>
        )}

        <div className={styles.buttonContainer}>
          <SecondaryButton
            buttonName='Cancel'
            onClick={handleClose}
            disabled={isUploading}
          />
          <PrimaryButton
            buttonName='Upload'
            onClick={handleUpload}
            disabled={!isFormValid || isUploading}
          />
        </div>
      </Box>
    </Modal>
  )
}

export default UploadModal
